<template>
  <div class="invoice-edit-view">
    <title-component
      :size="1" v-if="invoice.id">
      <span v-translate>Edit invoice</span>
      <action-dropdown-component
        v-if="actions.length"
        :actions="actions">
      </action-dropdown-component>
    </title-component>
    <!-- Customer -->
    <title-component
      v-if="customer"
      :size="3"
      :lineSeparator="'under'">
      <translate>Invoice customer</translate>
    </title-component>
    <grid-details-component
      v-if="customer && invoiceCustomer.fullAddress"
      :display_spec="invoiceCustomerSpecs"
      :item="invoiceCustomer"
      :item_keys="invoice_customer_keys">
    </grid-details-component>
    <!-- Loader - customer -->
    <loader-component v-else :position="'relative'"></loader-component>
    <!-- Messages -->
    <message-component v-if="customer.invoiceInformationMismatch" :level="'warning'">
      <link-component
        :name="'InvoicingView'"
        :params="{ customerId: customer.id }">
          <translate> Customers invoice information has changed. Please review the new information before sending any new invoices. </translate>
      </link-component>
    </message-component>
    <!-- Invoice -->
    <title-component
      v-if="customer"
      :size="3"
      :lineSeparator="'under'">
      <translate>Invoice</translate>
    </title-component>
    <!-- Invoice update form -->
    <invoice-form-component
      v-if="invoice.id"
      :invoice="invoice">
    </invoice-form-component>
    <!-- Modal (popup) for confirming cancellation -->
    <card-modal-component
      :show="showConfirmationModal"
      @deactivate="toggleConfirmationModal()"
      ref="confirmationModal"
      :title="'Confirmation'">
      <translate slot="card-content">
        Are you sure you want to cancel the invoice?
      </translate>
      <div class="field is-grouped" slot="card-footer">
        <div class="control">
          <submit-btn-component class="is-primary" slot="card-footer" @submit="cancelInvoice">Continue</submit-btn-component>
        </div>
        <div class="control">
          <button class="button is-danger is-outlined" slot="card-footer" @click="toggleConfirmationModal" v-translate>Cancel</button>
        </div>
      </div>
    </card-modal-component>
  </div>
</template>

<script>
  import { mapActions, mapState } from 'vuex'
  import { invoiceCustomerSpecs } from '@/display_context/display_context'
  import TitleComponent from '@/components/layout/TitleComponent'
  import GridDetailsComponent from '@/components/layout/grid/GridDetailsComponent'
  import ActionDropdownComponent from '@/components/layout/utility/ActionDropdownComponent.vue'
  import { itemRefresher } from '@/mixins/mixins'
  import { InvoiceState } from '@/classes/case/Invoice'
  import MessageComponent from '@/components/layout/utility/MessageComponent'
  import LinkComponent from '@/components/layout/utility/LinkComponent'
  import InvoiceFormComponent from '@/components/form/InvoiceFormComponent'
  import LoaderComponent from '@/components/layout/utility/LoaderComponent'
  import CardModalComponent from '@/components/layout/modal/CardModalComponent'
  import SubmitBtnComponent from '@/components/layout/utility/SubmitBtnComponent'

  export default {
    name: 'invoice-edit-view',
    mixins: [itemRefresher],
    data () {
      return {
        invoice_customer_keys: ['companyName', 'orgNumber', 'customerId', 'fullAddress'],
        invoiceCustomerSpecs: invoiceCustomerSpecs,
        actions: [],
        setAction: this.setInvoice,
        customer: {},
        invoiceCustomer: {},
        invoice: {},
        allowed_states: [InvoiceState.DRAFT],
        showConfirmationModal: false
      }
    },
    props: {
      invoiceId: {
        required: true
      }
    },
    components: {
      ActionDropdownComponent,
      TitleComponent,
      GridDetailsComponent,
      MessageComponent,
      LinkComponent,
      InvoiceFormComponent,
      LoaderComponent,
      CardModalComponent,
      SubmitBtnComponent
    },
    computed: {
      ...mapState({
        invoices: state => state.invoiceModule.invoices
      })
    },
    methods: {
      ...mapActions([
        'setInvoice',
        'setCustomer',
        'setInvoiceCustomer',
        'postConfirmInvoice',
        'postCancelInvoice'
      ]),
      toggleConfirmationModal () {
        this.showConfirmationModal = !this.showConfirmationModal
      },
      confirmInvoice () {
        this.postConfirmInvoice(this.invoiceId).then(invoice => {
          this.invoice = invoice
          this.setActions()
          this.$router.push({ name: 'InvoiceDetailsView', params: { invoiceId: this.invoiceId } })
        }).catch((err) => {
          this.handleError(err)
        })
      },
      cancelInvoice (btn) {
        this.postCancelInvoice(this.invoiceId).then(invoice => {
          this.invoice = invoice
          this.setActions()
          this.showConfirmationModal = false
          this.$router.push({ name: 'InvoiceDetailsView', params: { invoiceId: this.invoiceId } })
        }).catch((err) => {
          this.handleError(err)
        }).finally(() => {
          btn && btn.enable()
        })
      },
      setActions () {
        this.actions = []
        if (this.invoice.id) {
          this.actions.push({
            type: 'ACTION',
            func: () => {
              this.confirmInvoice()
            },
            text: `Confirm`
          })
          this.actions.push({
            type: 'ACTION',
            func: () => {
              this.toggleConfirmationModal()
            },
            text: `Cancel invoice`
          })
        }
      },
      setInvoiceCustomerProps () {
        this.invoiceCustomer.customerId = this.customer.customerId
        this.invoiceCustomer.fullAddress = `${this.invoiceCustomer.postalAddress}, ${this.invoiceCustomer.postalCode}, ${this.invoiceCustomer.postalCity}`
      },
      getInvoiceCustomer () {
        if (this.customer.isConnectedToInvoiceCustomer) {
          this.setInvoiceCustomer(this.customer.invoiceCustomerId).then((invoiceCustomer) => {
            this.invoiceCustomer = invoiceCustomer
            this.setInvoiceCustomerProps()
          }).catch((err) => {
            this.handleError(err)
          })
        }
      },
      getCustomer () {
        this.setCustomer(this.invoice.customerId).then((customer) => {
          this.customer = customer
          this.getInvoiceCustomer()
        }).catch((err) => {
          this.handleError(err)
        })
      },
      checkRedirect () {
        if (!this.invoice.hasValidStates(this.allowed_states)) {
          this.$router.replace({ name: 'InvoiceDetailsView', params: { invoiceId: this.invoice.id } })
          return
        }
        this.initPage()
      },
      initPage () {
        this.getCustomer()
        this.setActions()
      }
    },
    created () {
      this.setOrRefreshItem('invoice', this.invoices, this.invoiceId, [this.checkRedirect])
    }
  }
</script>

<style lang="sass">
  .invoice-edit-view
    overflow: auto
</style>
